import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, ChevronDown, Phone, Mail, Facebook, Instagram, Linkedin, Twitter, ChevronsRight, Bell, Sparkles, ExternalLink, RefreshCw, CheckCheck, WifiOff, LogIn } from 'lucide-react';
import { LOGO_URL, services, subServices } from '../data/mock';
import { useLiveNews } from '../hooks/useLiveNews';
import AISearch from './AISearch';

const navItems = [
  { label: 'Home', href: '/' },
  { label: 'About', href: '/about' },
  { label: 'Services', href: '/services', mega: true },
  {
    label: 'AMC',
    href: '/amc-for-brands',
    submenu: [
      { label: 'AMC for Brands', description: 'Renewals, products, vendors and regulatory tracking', href: '/amc-for-brands' },
      { label: 'AMC for Manufacturers', description: 'Factory licences, audits and compliance continuity', href: '/amc-for-manufacturers' },
    ],
  },
  { label: 'Industries', href: '/industries' },
  { label: 'Insights', href: '/insights' },
  { label: 'All Pages', href: '/pages', mobileOnly: true },
];

const NewsBar = ({ items }) => {
  if (!items || items.length === 0) return null;
  const doubled = [...items, ...items];
  return (
    <div className="hidden md:block bg-[#0A0E1A] text-white overflow-hidden border-b border-white/5">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-9 flex items-center gap-4">
        <span className="shrink-0 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest text-[#F87171]">
          <span className="relative flex items-center justify-center">
            <span className="w-1.5 h-1.5 rounded-full bg-[#F87171]" />
            <span className="absolute w-3 h-3 rounded-full bg-[#F87171]/40 animate-ping" />
          </span>
          Live
        </span>
        <div className="relative flex-1 overflow-hidden">
          <div className="news-marquee gap-10">
            {doubled.map((n, i) => (
              <span key={`${n.id}-${i}`} className="flex items-center gap-2.5 text-xs text-white/80 whitespace-nowrap">
                <span className="px-1.5 py-0.5 rounded bg-[#C41E3A] text-white text-[9px] font-bold tracking-wide">{n.tag}</span>
                {n.title} {n.desc ? <span className="text-white/50">— {n.desc}</span> : null}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const NotificationBell = ({ items, unread, onMarkRead, onMarkAllRead, onRefresh, refreshing, connection, lastUpdated }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const offline = connection === 'offline';
  const syncing = connection === 'connecting' || refreshing;

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative w-11 h-11 rounded-full border border-neutral-200 hover:border-[#C41E3A] hover:bg-rose-50 flex items-center justify-center transition-all group"
        aria-label="Notifications"
        aria-expanded={open}
      >
        <Bell className="w-4.5 h-4.5 text-neutral-800 group-hover:text-[#C41E3A] transition-colors" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#C41E3A] text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white">
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div className="mega-in absolute right-0 top-full mt-3 w-[380px] bg-white rounded-2xl shadow-2xl border border-neutral-200 overflow-hidden z-50">
          <div className="flex items-center justify-between p-4 border-b border-neutral-100">
            <div>
              <div className="font-display text-lg font-bold text-neutral-900">Notifications</div>
              <div className="text-[11px] text-neutral-500">
                {unread} unread · updated {lastUpdated?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
            <span className={`inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest ${offline ? 'text-amber-600' : 'text-emerald-600'}`}>
              {offline ? <WifiOff className="w-3.5 h-3.5" /> : <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />}
              {offline ? 'Offline' : syncing ? 'Syncing' : 'Active'}
            </span>
          </div>
          {unread > 0 && (
            <div className="flex justify-end px-4 py-2 bg-neutral-50/70 border-b border-neutral-100">
              <button
                type="button"
                onClick={onMarkAllRead}
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-[#C41E3A] hover:text-[#A81830]"
              >
                <CheckCheck className="w-3.5 h-3.5" /> Mark all as read
              </button>
            </div>
          )}
          <div className="max-h-[360px] overflow-y-auto">
            {items.length === 0 && (
              <div className="p-6 text-center text-sm text-neutral-500">No updates right now.</div>
            )}
            {items.map((n) => (
              <div
                key={n.id}
                onClick={() => n.unread && onMarkRead && onMarkRead(n.id)}
                onKeyDown={(event) => {
                  if ((event.key === 'Enter' || event.key === ' ') && n.unread && onMarkRead) onMarkRead(n.id);
                }}
                role="button"
                tabIndex={0}
                className={`group flex gap-3 p-4 hover:bg-rose-50/50 border-b border-neutral-50 last:border-b-0 cursor-pointer focus:outline-none focus:bg-rose-50/50 ${n.unread ? 'bg-rose-50/20' : 'bg-white'}`}
              >
                <span className={`shrink-0 mt-1 w-2 h-2 rounded-full ${n.unread ? 'bg-[#C41E3A]' : 'bg-neutral-300'}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-neutral-900 text-white tracking-wide">{n.tag}</span>
                    <span className="text-[10px] text-neutral-500">{n.time}</span>
                  </div>
                  <div className="text-sm font-semibold text-neutral-900 leading-snug mb-1 group-hover:text-[#C41E3A] transition-colors">
                    {n.title}
                  </div>
                  <div className="text-xs text-neutral-600 leading-relaxed line-clamp-2">{n.desc}</div>
                  {n.source_url && (
                    <a
                      href={n.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 mt-1.5 text-[10px] font-semibold text-[#C41E3A] hover:underline"
                    >
                      View source <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            className="w-full flex items-center justify-center gap-1.5 p-3 bg-neutral-50 hover:bg-neutral-100 disabled:opacity-60 text-xs font-semibold text-neutral-900 transition-colors"
          >
            Refresh updates <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      )}
    </div>
  );
};

const ServicesMega = ({ onClose, onEnter, onLeave }) => {
  const [active, setActive] = useState(services[0]);
  const subs = subServices[active.code] || [];

  return (
    <div
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      className="mega-in absolute left-0 right-0 top-full mt-2 px-6 lg:px-10 z-50"
    >
      <div className="max-w-[1200px] mx-auto bg-[#0A0E1A] rounded-3xl shadow-2xl border border-white/10 overflow-hidden">
      <div className="grid grid-cols-12 gap-0">
        {/* Left: services list */}
        <div className="col-span-5 border-r border-white/10 py-6">
          <div className="px-6 mb-4 text-[10px] uppercase tracking-widest text-white/40 font-semibold">Services</div>
          <div className="max-h-[480px] overflow-y-auto pr-3 pl-3">
            {services.map((s, i) => {
              const isActive = active.code === s.code;
              return (
                <button
                  key={s.code}
                  onMouseEnter={() => setActive(s)}
                  className={`w-full group flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors text-left ${
                    isActive ? 'bg-[#C41E3A] text-white' : 'text-white/85 hover:bg-white/5'
                  }`}
                >
                  <span className={`w-9 h-9 shrink-0 rounded-lg flex items-center justify-center overflow-hidden p-1.5 ${
                    isActive ? 'bg-white/15' : 'bg-white/[0.06]'
                  }`}>
                    <img src={s.logo} alt={s.code} className="w-full h-full object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  </span>
                  <span className={`text-[10px] font-mono w-6 ${isActive ? 'text-white/70' : 'text-white/40'}`}>{String(i + 1).padStart(2, '0')}</span>
                  <span className="flex-1 text-sm font-medium leading-tight">{s.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Right: active details */}
        <div className="col-span-7 p-8">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#F87171] font-semibold mb-3">
            <span className="w-1.5 h-1.5 rounded-full bg-[#F87171]" /> Authority focus
          </div>
          <h3 className="font-display text-3xl font-extrabold text-white leading-tight mb-3">{active.name}</h3>
          <p className="text-sm text-white/60 leading-relaxed max-w-lg mb-6">{active.desc}</p>

          <div className="grid sm:grid-cols-2 gap-3 mb-6">
            {subs.map((sub) => {
              const item = typeof sub === 'string' ? { label: sub, href: active.href } : sub;
              return (
              <a
                key={item.href}
                href={item.href}
                onClick={onClose}
                className="group flex items-center gap-3 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-[#C41E3A]/50 px-4 py-3 transition-all"
              >
                <span className="w-9 h-9 shrink-0 rounded-lg bg-white flex items-center justify-center overflow-hidden p-1.5">
                  <img src={active.logo} alt={active.code} className="w-full h-full object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                </span>
                <span className="flex-1 text-sm font-semibold text-white leading-tight group-hover:text-[#F87171] transition-colors">{item.label}</span>
                <ChevronsRight className="w-4 h-4 text-white/40 group-hover:text-[#F87171] group-hover:translate-x-1 transition-all" />
              </a>
              );
            })}
          </div>

          <div className="flex items-center gap-3">
            <a
              href="/contact"
              onClick={onClose}
              className="pill-btn group inline-flex items-center gap-2 rounded-full bg-[#C41E3A] hover:bg-[#A81830] text-white pl-5 pr-1 h-11 text-xs font-semibold"
            >
              Talk to advisor
              <span className="w-8 h-8 rounded-full bg-white flex items-center justify-center pill-arrow">
                <ChevronsRight className="w-3.5 h-3.5 text-[#C41E3A]" />
              </span>
            </a>
            <a href="/pages" onClick={onClose} className="text-xs text-white/60 hover:text-white link-underline">Browse all services</a>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const megaTimer = useRef(null);
  const {
    notifications,
    ticker,
    unread,
    markRead,
    markAllRead,
    refetch,
    refreshing,
    connection,
    lastUpdated,
  } = useLiveNews();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const enterMega = () => {
    if (megaTimer.current) clearTimeout(megaTimer.current);
    setMegaOpen(true);
  };
  const leaveMega = () => {
    megaTimer.current = setTimeout(() => setMegaOpen(false), 150);
  };

  return (
    <>
      {/* Top LIVE news bar */}
      <NewsBar items={ticker} />

      {/* Contact bar */}
      <div className="hidden lg:block bg-[#C41E3A] text-white text-xs">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-9 flex items-center justify-between">
          <div className="flex items-center gap-5">
            <a href="tel:+918851590081" className="flex items-center gap-1.5 hover:text-white/80">
              <Phone className="w-3.5 h-3.5" /> 8851590081
            </a>
            <a href="tel:+918076349604" className="flex items-center gap-1.5 hover:text-white/80">
              <Phone className="w-3.5 h-3.5" /> +91 8076349604
            </a>
            <a href="mailto:info@compliseindia.com" className="flex items-center gap-1.5 hover:text-white/80">
              <Mail className="w-3.5 h-3.5" /> info@compliseindia.com
            </a>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-white/80">Follow Us:</span>
            <a href="#" className="hover:text-white/80"><Facebook className="w-3.5 h-3.5" /></a>
            <a href="#" className="hover:text-white/80"><Twitter className="w-3.5 h-3.5" /></a>
            <a href="#" className="hover:text-white/80"><Instagram className="w-3.5 h-3.5" /></a>
            <a href="#" className="hover:text-white/80"><Linkedin className="w-3.5 h-3.5" /></a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <header
        className={`sticky top-0 z-40 transition-all duration-500 ${
          scrolled ? 'bg-white/95 backdrop-blur-xl shadow-sm' : 'bg-white'
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between relative">
          <a href="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Complise India" className="h-10 md:h-12 w-auto" />
          </a>

          <nav className="hidden xl:flex items-center gap-0">
            {navItems.filter((item) => !item.mobileOnly).map((item) => (
              <div
                key={item.label}
                className="relative group/amc"
                onMouseEnter={() => item.mega && enterMega()}
                onMouseLeave={() => item.mega && leaveMega()}
              >
                <a
                  href={item.href}
                  className={`group flex items-center gap-1 text-[14px] font-medium px-3 py-2 rounded-full transition-colors ${
                    item.mega && megaOpen ? 'text-[#C41E3A] bg-rose-50' : 'text-neutral-800 hover:text-[#C41E3A] hover:bg-rose-50/60'
                  }`}
                >
                  {item.label}
                  {(item.mega || item.submenu) && <ChevronDown className={`w-3.5 h-3.5 opacity-60 transition-transform ${item.mega && megaOpen ? 'rotate-180' : ''}`} />}
                </a>
                {item.submenu && (
                  <div className="invisible opacity-0 translate-y-2 group-hover/amc:visible group-hover/amc:opacity-100 group-hover/amc:translate-y-0 group-focus-within/amc:visible group-focus-within/amc:opacity-100 group-focus-within/amc:translate-y-0 absolute left-1/2 top-full z-50 w-[360px] -translate-x-1/2 pt-3 transition-all duration-200">
                    <div className="rounded-2xl border border-neutral-200 bg-white p-2 shadow-2xl">
                      {item.submenu.map((subitem) => (
                        <a key={subitem.href} href={subitem.href} className="block rounded-xl px-4 py-3 hover:bg-rose-50 transition-colors">
                          <span className="block text-sm font-bold text-neutral-900">{subitem.label}</span>
                          <span className="mt-1 block text-xs leading-relaxed text-neutral-500">{subitem.description}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="hidden xl:flex items-center gap-2">
            <a
              href="https://crm.compliseindia.com"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 rounded-full border border-neutral-200 hover:border-neutral-900 hover:bg-neutral-900 text-neutral-800 hover:text-white px-4 h-11 text-sm font-semibold transition-all"
            >
              <LogIn className="w-4 h-4" />
              Portal Login
            </a>
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="group inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50/70 hover:bg-[#C41E3A] hover:border-[#C41E3A] text-[#C41E3A] hover:text-white px-4 h-11 text-sm font-semibold transition-all"
            >
              <Sparkles className="w-4 h-4 transition-transform group-hover:rotate-12" />
              AI Search
            </button>
            <NotificationBell
              items={notifications}
              unread={unread}
              onMarkRead={markRead}
              onMarkAllRead={markAllRead}
              onRefresh={refetch}
              refreshing={refreshing}
              connection={connection}
              lastUpdated={lastUpdated}
            />
            <a
              href="/contact"
              className="pill-btn group inline-flex items-center gap-2 rounded-full bg-[#C41E3A] hover:bg-[#A81830] text-white pl-5 pr-1 h-11 text-sm font-semibold transition-colors"
            >
              Free Consult
              <span className="w-8 h-8 rounded-full bg-white flex items-center justify-center pill-arrow">
                <ChevronsRight className="w-3.5 h-3.5 text-[#C41E3A]" />
              </span>
            </a>
          </div>

          <div className="xl:hidden flex items-center gap-3">
            <NotificationBell
              items={notifications}
              unread={unread}
              onMarkRead={markRead}
              onMarkAllRead={markAllRead}
              onRefresh={refetch}
              refreshing={refreshing}
              connection={connection}
              lastUpdated={lastUpdated}
            />
            <button className="text-neutral-900" onClick={() => setOpen(!open)} aria-label="Toggle menu">
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        {/* Mega menu - positioned relative to sticky header, spans full width */}
        {megaOpen && (
          <ServicesMega
            onClose={() => setMegaOpen(false)}
            onEnter={enterMega}
            onLeave={leaveMega}
          />
        )}

        {open && (
          <div className="xl:hidden bg-white border-t border-neutral-100 px-6 py-6">
            <nav className="flex flex-col gap-4">
              {navItems.map((item) => item.submenu ? (
                <div key={item.label} className="border-b border-neutral-100 pb-3">
                  <div className="mb-2 text-lg font-semibold text-neutral-900">{item.label}</div>
                  <div className="grid gap-2 pl-3">
                    {item.submenu.map((subitem) => (
                      <a key={subitem.href} href={subitem.href} onClick={() => setOpen(false)} className="text-sm font-medium text-neutral-600 hover:text-[#C41E3A]">
                        {subitem.label}
                      </a>
                    ))}
                  </div>
                </div>
              ) : (
                <a key={item.label} href={item.href} onClick={() => setOpen(false)} className="text-neutral-800 text-lg font-medium">
                  {item.label}
                </a>
              ))}
              <button
                type="button"
                onClick={() => { setOpen(false); setSearchOpen(true); }}
                className="flex items-center gap-2 text-left text-lg font-semibold text-[#C41E3A]"
              >
                <Sparkles className="w-5 h-5" /> AI Search
              </button>
              <a
                href="https://crm.compliseindia.com"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2 text-left text-lg font-semibold text-neutral-900"
              >
                <LogIn className="w-5 h-5 text-[#C41E3A]" /> Portal Login
              </a>
              <a href="tel:+918851590081" className="flex items-center gap-2 text-[#C41E3A] mt-2">
                <Phone className="w-4 h-4" /> 8851590081
              </a>
              <a href="tel:+918076349604" className="flex items-center gap-2 text-[#C41E3A]">
                <Phone className="w-4 h-4" /> +91 8076349604
              </a>
              <a href="/contact" onClick={() => setOpen(false)} className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#C41E3A] text-white px-5 h-11 text-sm font-semibold">
                Free Consult
              </a>
            </nav>
          </div>
        )}
      </header>
      <AISearch open={searchOpen} onOpenChange={setSearchOpen} />
    </>
  );
};

export default Header;
